/**
 * Firmio Meet queries and mutations for the colleagues' pages.
 *
 * The in-call session (LiveKit room, waiting-room poll, transcript) is not
 * here: it lives in useMeetRoom.ts and is page-scoped, because it runs for
 * guests who have no react-query cache worth invalidating.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import { newArrivals, nextAdmitRequest, pruneHidden } from '../lib/meetAdmitQueue';
import { playSound } from '../lib/sounds';
import {
  admitParticipant,
  cancelMeeting,
  createMeeting,
  deleteMeeting,
  denyParticipant,
  endMeeting,
  fetchMeeting,
  fetchMeetingChat,
  fetchMeetings,
  fetchMeetPublic,
  fetchWaiting,
  updateMeeting,
  writeMeetingLog,
  type CreateMeetingInput,
  moderateParticipant,
  type MeetModeration,
  type MeetParticipant,
} from '../lib/meetApi';

export const MEET_KEY = ['meet'] as const;

export function useMeetings(input: {
  tab?: 'upcoming' | 'past';
  from?: string;
  to?: string;
  limit?: number;
  q?: string;
  companyId?: string | null;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      ...MEET_KEY,
      'list',
      input.tab ?? null,
      input.from ?? null,
      input.to ?? null,
      input.limit ?? null,
      input.q?.trim() || null,
      input.companyId ?? null,
    ],
    queryFn: () =>
      fetchMeetings({
        tab: input.tab,
        from: input.from,
        to: input.to,
        limit: input.limit,
        q: input.q,
        companyId: input.companyId,
      }),
    enabled: input.enabled ?? true,
    staleTime: 15_000,
    // Typing in the search keeps the old list on screen until the new one lands.
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    // A live meeting on the list should turn into a past one on its own.
    refetchInterval: input.tab === 'upcoming' ? 30_000 : false,
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: [...MEET_KEY, 'detail', id],
    queryFn: () => fetchMeeting(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

/** What was typed during a meeting, for the history page. */
export function useMeetingChat(id: string | undefined) {
  return useQuery({
    queryKey: [...MEET_KEY, 'chat', id],
    queryFn: () => fetchMeetingChat(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

/**
 * The lobby's view of a meeting. Polls while the link is not open yet, so the
 * page unlocks itself at the scheduled time without a refresh.
 */
export function useMeetPublicInfo(code: string | undefined) {
  return useQuery({
    queryKey: [...MEET_KEY, 'public', code],
    queryFn: () => fetchMeetPublic(code!),
    enabled: Boolean(code),
    staleTime: 10_000,
    retry: false,
    refetchInterval: (query) => {
      const info = query.state.data;
      if (!info) return false;
      if (info.status === 'ENDED' || info.status === 'CANCELLED') return false;
      return info.joinable ? 30_000 : 15_000;
    },
  });
}

/** The waiting room, for colleagues inside the call. Polls every few seconds. */
export function useWaitingRoom(meetingId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [...MEET_KEY, 'waiting', meetingId],
    queryFn: () => fetchWaiting(meetingId!),
    enabled: Boolean(meetingId) && enabled,
    staleTime: 0,
    refetchInterval: enabled ? 4_000 : false,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: MEET_KEY });
}

export function useCreateMeeting() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateMeetingInput) => createMeeting(input),
    onSettled: () => void invalidate(),
  });
}

export function useUpdateMeeting() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Parameters<typeof updateMeeting>[1]) =>
      updateMeeting(id, patch),
    onSettled: () => void invalidate(),
  });
}

export function useEndMeeting() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => endMeeting(id),
    onSettled: () => void invalidate(),
  });
}

export function useCancelMeeting() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => cancelMeeting(id),
    onSettled: () => void invalidate(),
  });
}

export function useDeleteMeeting() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => deleteMeeting(id),
    onSettled: () => void invalidate(),
  });
}

export function useWriteMeetingLog() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => writeMeetingLog(id),
    onSettled: () => void invalidate(),
  });
}

export function useAdmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      meetingId,
      participantId,
      decision,
    }: {
      meetingId: string;
      participantId: string;
      decision: 'admit' | 'deny';
    }) =>
      decision === 'admit'
        ? admitParticipant(meetingId, participantId)
        : denyParticipant(meetingId, participantId),
    onSettled: (_r, _e, vars) =>
      void qc.invalidateQueries({ queryKey: [...MEET_KEY, 'waiting', vars.meetingId] }),
  });
}

/**
 * The full-screen "X wants to join" prompt (components/meet/AdmitRequestOverlay),
 * for every colleague in the call: anyone admitted can admit, and the first
 * click wins. Each new arrival knocks once (lib/sounds.ts `knock`).
 *
 * The prompt moves on at once after Acceptă / Respinge, without waiting for the
 * next poll. A request another colleague already answered comes back as 404
 * PARTICIPANT_NOT_WAITING, which simply means "done". Any other failure
 * brings the prompt back, so nobody is left at the door by a network blip.
 */
export function useAdmitPrompt(
  meetingId: string | null,
  waiting: readonly MeetParticipant[],
  enabled: boolean,
) {
  const qc = useQueryClient();
  const [later, setLater] = useState<ReadonlySet<string>>(() => new Set());
  const [handled, setHandled] = useState<ReadonlySet<string>>(() => new Set());
  const seen = useRef<ReadonlySet<string> | null>(null);

  useEffect(() => {
    if (!enabled) {
      seen.current = null;
      return;
    }
    if (newArrivals(seen.current, waiting).length > 0) playSound('knock');
    seen.current = new Set(waiting.map((p) => p.id));
    setLater((prev) => pruneHidden(prev, waiting));
    setHandled((prev) => pruneHidden(prev, waiting));
  }, [enabled, waiting]);

  const hidden = useMemo(() => new Set([...later, ...handled]), [later, handled]);
  const prompt = enabled ? nextAdmitRequest(waiting, hidden) : null;

  const decide = useCallback(
    (participantId: string, decision: 'admit' | 'deny') => {
      if (!meetingId) return;
      setHandled((prev) => new Set([...prev, participantId]));
      const call = decision === 'admit' ? admitParticipant : denyParticipant;
      call(meetingId, participantId)
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'PARTICIPANT_NOT_WAITING') return;
          setHandled((prev) => new Set([...prev].filter((id) => id !== participantId)));
        })
        .finally(() => {
          void qc.invalidateQueries({ queryKey: [...MEET_KEY, 'waiting', meetingId] });
        });
    },
    [meetingId, qc],
  );

  return {
    prompt,
    accept: (participantId: string) => decide(participantId, 'admit'),
    deny: (participantId: string) => decide(participantId, 'deny'),
    acceptAll: () => {
      for (const p of waiting) decide(p.id, 'admit');
    },
    later: (participantId: string) => setLater((prev) => new Set([...prev, participantId])),
  };
}

/**
 * Mute / camera off / remove, from the people panel. Nothing to invalidate:
 * the result reaches the panel through LiveKit itself (the track mutes, the
 * participant disconnects), not through a query.
 */
export function useModeration() {
  return useMutation({
    mutationFn: ({
      meetingId,
      participantId,
      action,
    }: {
      meetingId: string;
      participantId: string;
      action: MeetModeration;
    }) => moderateParticipant(meetingId, participantId, action),
  });
}
